import { Fragment, memo, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import type { ClipboardEvent, DragEvent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api';
import type { Card, ModelChoices, ThreadUsage } from '../api';
import * as perf from '../utils/switch_perf';
import { CardBodyMarkdown } from './cards/CardBodyMarkdown';
import { RefsPopover } from './RefsPopover';
import { TokenUsageChip } from './TokenUsageChip';

/** Stable empty fallback for `knownIds` when no cards are available yet.
 *  Hoisted to module scope so its identity stays the same across renders
 *  and doesn't churn `CardBodyMarkdown`'s memoization. */
const EMPTY_ID_SET: ReadonlySet<string> = new Set();

export type ChatMessage =
  | { kind: 'user'; text: string; ts: number; turnId?: string }
  /** Assistant text bubble. `truncated` is set when the LLM hit `max_tokens`
   *  for this message — UI shows a warning so the user knows the response
   *  is incomplete and can retry / raise DRAFTLIZE_MAX_TOKENS. */
  | { kind: 'assistant'; text: string; ts: number; truncated?: boolean; turnId?: string }
  | { kind: 'tool_call'; tool: string; input: string; ts: number; turnId?: string }
  /** Reasoning stream from the model. During a live turn this is appended
   *  by `thinking_delta` SSE events with `done: false`; flips to `done:
   *  true` on `thinking_end`. After reload the runtime persists thinking
   *  as `ContentBlock::Thinking`, and we hydrate those into bubbles with
   *  `done: true` so the same collapsed UI renders for historical turns. */
  | { kind: 'thinking'; text: string; done: boolean; ts: number };

export interface DiscussionPaneProps {
  /** Active project id. Drives ref uploads from drag-drop on the
   *  composer; null while no project is selected (composer is hidden in
   *  that case anyway). */
  projectId: string | null;
  /** Active thread id. Used to detect deferred-lag — when `threadId`
   *  changes the messages prop will catch up a frame or two later, and
   *  the comparison `threadId !== useDeferredValue(threadId)` lets us
   *  paint nothing during the gap rather than the previous thread's
   *  bubbles. */
  threadId: string | null;
  messages: ChatMessage[];
  /** PR-55: per-thread token-usage snapshot for the context bar.
   *  Hydrated by `App` from `getThreadUsage` on thread open and
   *  refreshed on each `turn_usage` SSE event. Null when no thread
   *  is active or the snapshot hasn't loaded yet — bar is hidden. */
  usage?: ThreadUsage | null;
  /** PR-56: active thread's model override (`threads.model`). Null
   *  means "use server default" — the chip renders the default
   *  string from `modelChoices.default`. PR-57: this is a tier id. */
  threadModel?: string | null;
  /** PR-57: graded reasoning effort for tiers that support it. */
  threadEffort?: string | null;
  /** PR-57: thread fast-mode flag. */
  threadFastMode?: boolean;
  /** PR-56: vision-sticky state for the active thread. When true,
   *  the user's pick gets silently overridden to the vision model
   *  by the server; the chip shows a 👁 marker so this isn't a
   *  surprise. */
  threadHadAttachment?: boolean;
  /** PR-56: server's model picker list. Null while loading. */
  modelChoices?: ModelChoices | null;
  /** PR-57: write the full preference in one call. Each field is
   *  independently optional — pass only the ones the user changed. */
  onChangeModelPreference?: (patch: {
    model?: string | null;
    effort?: string | null;
    fast_mode?: boolean;
  }) => void;
  onSend: (text: string, options?: { imageAttachments?: string[] }) => void;
  busy: boolean;
  /** Stop the in-flight turn. Null when no turn is running (or the
   *  parent hasn't captured its id yet) — the composer's stop button
   *  hides when this is null. Click is fire-and-forget; the canonical
   *  outcome arrives via the next SSE error/turn_done events. */
  onStopTurn?: (() => void) | null;
  /** Snapshot of the active thread's queued-send slot. The user pressed Enter
   *  while a previous turn was still streaming; the message is captured here
   *  and auto-fired when busy clears. Null when no queue is pending. */
  queuedSend?: { text: string; imageRefs: string[] } | null;
  /** Cancel the active thread's queued send. Called from the chip's × button. */
  onCancelQueuedSend?: () => void;
  /** Open the distill flow modal. Disabled when there are no messages or
   *  while the agent is busy (no point distilling a partial state). */
  onDistill?: () => void;
  /** True while a distill request is in flight; the button shows progress. */
  distilling?: boolean;
  /** Turn ids to highlight (e.g. while the user hovers a Gap Ledger card
   *  with `extra.context.turns`). Bubbles whose `turnId` is in the set
   *  get a soft amber outline and the first one is scrolled into view. */
  highlightTurnIds?: ReadonlySet<string>;
  /** Gap card the user clicked Discuss on. While set, a banner sits above
   *  the message stream and the next user message is sent with a
   *  `[discussing-gap c-X]` hint so the agent stays focused on resolving it. */
  pinnedGap?: Card | null;
  /** Resolve / unpin the focused gap. The gap card itself is NOT deleted —
   *  the user (or agent, via Approve / Reject) handles deletion. */
  onResolveGap?: () => void;
  /** Approve the pinned gap from inside the banner — same effect as
   *  clicking Approve in the GapLedger row (promote → proposed_kind). */
  onApprovePinnedGap?: (cardId: string) => Promise<void> | void;
  /** Reject the pinned gap from inside the banner — same effect as
   *  clicking Reject in the GapLedger row (delete + dismissed_gaps). */
  onRejectPinnedGap?: (cardId: string) => Promise<void> | void;
  /** When set, the discussion scrolls to the bubble whose `turnId` matches.
   *  `nonce` lets the parent re-trigger the same id (consecutive clicks on
   *  the same cited turn): we depend on the whole object so a new reference
   *  with the same id still fires the effect. */
  jumpToTurn?: { turnId: string; nonce: number } | null;
  /** When set, the user just clicked Resolve on the pinned gap with this id;
   *  show a one-shot banner asking whether to run an audit (the discussion
   *  may have surfaced new gaps). Cleared by the parent once the user picks
   *  Run Audit or Dismiss. */
  postResolveAuditFor?: string | null;
  onPostResolveAudit?: () => void;
  onDismissPostResolveAudit?: () => void;
  /** Inline "Convert to gaps" affordance — fires when the user clicks
   *  the button on a listicle-shaped agent reply. Parent runs decompose
   *  distill scoped to that single message and opens the review modal.
   *  Receives the message's turn id (e.g. "t-42"); the parent converts
   *  that to a session-message index for the API. */
  onConvertToGaps?: (turnId: string) => void;
  /** True while a convert-to-gaps run is in flight; the inline button
   *  greys out so we don't double-fire. */
  convertingToGaps?: boolean;
  /** Live cards, mirrored from the right panel. Lets a `propose_card` /
   *  `write_card` tool-call row resolve which card it produced so clicking
   *  the row scrolls the cards panel to it and opens its peek popover.
   *  Also feeds wikilink rendering inside agent bubbles — `[[c-X]]`
   *  mentions become clickable jump links when their id is in `cards`. */
  cards?: Card[];
  /** Fired when the user clicks a tool-call row that resolved to a card.
   *  Parent selects + flashes + opens the peek; anchor is the chat row's
   *  bounding rect (CardPeek positions next to it, falling back to centered
   *  when there isn't room to the left). */
  onJumpToCard?: (cardId: string, anchor: DOMRect) => void;
  /** Fired when the user clicks a `[[c-X]]` wikilink inside an agent
   *  bubble. Just selects the card (cards panel scrolls into view); no
   *  peek — keeps the user in their reading flow. Distinct from
   *  `onJumpToCard` which is the heavyweight peek-opening jump for
   *  deliberate tool-call inspections. */
  onSelectCard?: (cardId: string) => void;
  /** Inline approve for a `propose_card` row whose target is still kind=gap.
   *  Same effect as Approve in the Gap Ledger — promote → proposed_kind. */
  onApproveProposal?: (cardId: string) => Promise<void> | void;
  /** Bulk variant — promotes a list of gap cards in parallel and surfaces
   *  an [Undo] toast. Wired into the burst summary block (≥3 propose_card
   *  in one assistant bubble). Returns `{ ok, failed }` so the block can
   *  collapse / mark its row state. When omitted, the bulk block falls
   *  back to sequential `onApproveProposal` calls (no undo). */
  onApproveProposalBatch?: (
    cardIds: string[],
  ) => Promise<{ ok: string[]; failed: string[] }>;
  /** Inline reject. Same effect as Reject in the Gap Ledger — delete + mark
   *  `dismissed_gaps` on every card in `blocks`. */
  onRejectProposal?: (cardId: string) => Promise<void> | void;
  /** Inline discuss — spins up (or jumps to) a thread pinned to this gap. */
  onDiscussProposal?: (cardId: string) => void;
  /** Pixel width set by the user via the column resizer. When omitted,
   *  falls back to the original flex 540–760 elastic behavior. Parent is
   *  responsible for clamping. */
  width?: number;
}

export function DiscussionPane({
  projectId,
  threadId,
  messages,
  usage,
  threadModel,
  threadEffort,
  threadFastMode,
  threadHadAttachment,
  modelChoices,
  onChangeModelPreference,
  onSend,
  busy,
  onStopTurn,
  queuedSend,
  onCancelQueuedSend,
  onDistill,
  distilling,
  highlightTurnIds,
  pinnedGap,
  onResolveGap,
  onApprovePinnedGap,
  onRejectPinnedGap,
  jumpToTurn,
  postResolveAuditFor,
  onPostResolveAudit,
  onDismissPostResolveAudit,
  onConvertToGaps,
  convertingToGaps,
  cards,
  onJumpToCard,
  onSelectCard,
  onApproveProposal,
  onApproveProposalBatch,
  onRejectProposal,
  onDiscussProposal,
  width,
}: DiscussionPaneProps) {
  const { t } = useTranslation();
  perf.renderMark('DiscussionPane', { messages: messages.length });
  // Defer the heavy 50-200 message bubble render so the chrome (header,
  // model chip, scroll position) commits in the same frame as the
  // thread click. The deferred value lags by one render — if we used
  // it directly during a thread switch, we'd briefly paint the PREV
  // thread's bubbles under the new thread's chrome. The threadId
  // guard turns that gap into "render nothing" for the one frame
  // until deferred catches up.
  const deferredMessagesRaw = useDeferredValue(messages);
  const deferredThreadId = useDeferredValue(threadId);
  const isThreadLagging = threadId !== deferredThreadId;
  const renderMessages = isThreadLagging ? [] : deferredMessagesRaw;
  const scrollRef = useRef<HTMLDivElement>(null);
  // Tracks whether the user is "tailing" — i.e. close enough to the bottom that
  // they want to keep seeing new content. Updated lazily from onScroll. Default
  // true so the initial-load jump and the user's own send always land at the
  // bottom even before any onScroll has fired.
  const pinnedRef = useRef(true);
  const prevLenRef = useRef(0);
  // Tracks the threadId we last applied unread-tracking math against. On
  // a thread switch, `prevLenRef` still holds the PREVIOUS thread's
  // length — using `curr - prev` would mis-attribute the entire new
  // thread as "unread", firing setUnread(N) → a full Pass 2 re-render
  // ~300ms after paint. Comparing threadId here resets cleanly.
  const prevThreadIdRef = useRef<string | null>(null);
  // Set by the thread-switch branch in the [messages, threadId] effect:
  // "I want to land at the bottom once the deferred-rendered bubbles
  // commit to the DOM". Consumed by the [renderMessages] effect below.
  // Without this two-step dance, jumpToBottom fires when the bubbles
  // don't yet exist (useDeferredValue is still holding the old value),
  // scrollHeight is still small, and the user lands near the TOP of the
  // newly-loaded thread instead of the latest message.
  const pendingScrollToBottomRef = useRef(false);
  const [unread, setUnread] = useState(0);

  // Project-wide known card ids. Drives wikilink rendering inside agent
  // bubbles: only `[[c-X]]` whose id is in this set becomes a clickable
  // jump link; unknown ids stay as plain text (broken-ref guard).
  const knownIds = useMemo(
    () => new Set((cards ?? []).map((c) => c.card_id)),
    [cards],
  );

  // For each agent text bubble, collect the immediately-preceding run of
  // propose_card tool_calls whose target is still kind=gap. Threshold is
  // ≥3 — at 2, two inline ToolCallBadges with their own buttons read
  // cleaner than a summary block; at 3+ the column gets noisy and a
  // single summary with a single "Accept all" affordance wins. Thinking bubbles
  // between tool_calls are skipped (they're part of the same agent
  // burst). Streak resets on a user message, a non-propose_card
  // tool_call, or a write_card.
  //
  // The same useMemo also produces:
  //   - `burstOwnerByMessageIdx`: any message-idx in a collapsed burst
  //     (propose_card AND any thinking interleaved with them) → owning
  //     assistantIdx. The render loop checks this to hide everything
  //     between the first propose_card and the wrap-up assistant bubble
  //     when the burst is collapsed. Without including thinking, an
  //     orphaned "✦ thinking" line could appear between two hidden
  //     propose_card rows.
  const { bulkPendingByMessageIdx, burstOwnerByMessageIdx } = useMemo(() => {
    const bulk = new Map<number, string[]>();
    const owner = new Map<number, number>();
    if (!cards || cards.length === 0 || !onApproveProposal) {
      return { bulkPendingByMessageIdx: bulk, burstOwnerByMessageIdx: owner };
    }
    const cardById = new Map(cards.map((c) => [c.card_id, c]));
    for (let i = 0; i < renderMessages.length; i++) {
      const msg = renderMessages[i];
      if (msg.kind !== 'assistant') continue;
      const collected: string[] = [];
      const burstIndices: number[] = [];
      for (let j = i - 1; j >= 0; j--) {
        const prev = renderMessages[j];
        if (prev.kind === 'thinking') {
          // Track but don't count toward the burst size.
          burstIndices.unshift(j);
          continue;
        }
        if (prev.kind !== 'tool_call') break;
        if (prev.tool !== 'propose_card') break;
        const id = resolveCardForToolCall(prev.tool, prev.input, cards);
        if (!id) break;
        const card = cardById.get(id);
        if (!card || card.kind !== 'gap') break;
        collected.unshift(id);
        burstIndices.unshift(j);
      }
      if (collected.length >= 3) {
        bulk.set(i, collected);
        // Trim leading thinking indices that aren't sandwiched between
        // propose_cards (e.g. a stray thinking before the first
        // propose_card shouldn't be hidden — it might be commentary on
        // an earlier turn).
        let firstProposeIdx = -1;
        for (const idx of burstIndices) {
          const m = renderMessages[idx];
          if (m.kind === 'tool_call' && m.tool === 'propose_card') {
            firstProposeIdx = idx;
            break;
          }
        }
        if (firstProposeIdx >= 0) {
          for (const idx of burstIndices) {
            if (idx >= firstProposeIdx) owner.set(idx, i);
          }
        }
      }
    }
    return { bulkPendingByMessageIdx: bulk, burstOwnerByMessageIdx: owner };
  }, [renderMessages, cards, onApproveProposal]);

  // Per-burst expand state (keyed by the assistant message index that
  // owns the burst). When in `expandedBursts`, the individual
  // ToolCallBadges show again and the bulk block collapses to a thin
  // [collapse] toggle. Default: every burst starts collapsed (the spec's
  // "default to a summary block" behavior).
  const [expandedBursts, setExpandedBursts] = useState<Set<number>>(new Set());
  const toggleBurstExpanded = (assistantIdx: number) => {
    setExpandedBursts((prev) => {
      const next = new Set(prev);
      if (next.has(assistantIdx)) next.delete(assistantIdx);
      else next.add(assistantIdx);
      return next;
    });
  };

  const isAtBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const jumpToBottom = (smooth: boolean) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    pinnedRef.current = true;
    setUnread(0);
  };

  // Single effect drives auto-tail. We never smooth-scroll on arrival — the
  // animation steals attention from whatever the user is reading. Rules:
  //   - 0 → N (initial load / thread switch): instant jump, no pill.
  //   - last message is `user`: the user just sent something → instant jump.
  //   - length grew while pinned: instant jump (keep tail visible).
  //   - length grew while scrolled up: increment unread, show pill.
  //   - length unchanged (text streaming into existing bubble) + pinned:
  //     keep scrollTop = scrollHeight so the streaming text stays in view.
  useEffect(() => {
    const prev = prevLenRef.current;
    const curr = messages.length;
    prevLenRef.current = curr;

    // THREAD SWITCH: the prev/curr length diff is meaningless across
    // threads — `prev` is the OTHER thread's count. Treat as a fresh
    // mount: defer scroll-to-bottom until the deferred-rendered
    // bubbles commit to the DOM (handled by the [renderMessages]
    // effect below — calling jumpToBottom synchronously here would
    // snapshot scrollHeight before the bubbles exist and land the
    // user near the TOP of new content). Clear unread (no-op when
    // already 0 — only fires re-render when there was leftover from
    // the previous thread).
    if (prevThreadIdRef.current !== threadId) {
      prevThreadIdRef.current = threadId;
      if (curr > 0) {
        pendingScrollToBottomRef.current = true;
        pinnedRef.current = true;
      }
      setUnread(0);
      return;
    }

    if (prev === 0 && curr > 0) {
      // 0 → N within the same thread (initial fetch landed for a
      // freshly-opened cache-miss thread). Same deferred-render
      // problem — bubbles aren't in DOM yet. Defer.
      pendingScrollToBottomRef.current = true;
      pinnedRef.current = true;
      return;
    }

    if (curr > prev) {
      const last = messages[curr - 1];
      if (last?.kind === 'user') {
        jumpToBottom(false);
        return;
      }
      if (pinnedRef.current) {
        jumpToBottom(false);
      } else {
        const added = curr - prev;
        setUnread((n) => n + added);
      }
      return;
    }

    // Length unchanged: streaming delta into the last bubble. Keep tail visible
    // for pinned readers; do nothing if they've scrolled up.
    if (pinnedRef.current) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, threadId]);

  // Companion to the [messages, threadId] effect's thread-switch /
  // 0→N branches: those branches stash `pendingScrollToBottomRef`
  // because the bubbles haven't yet hit the DOM (useDeferredValue is
  // holding the old list). Here we react when the deferred catches up
  // (renderMessages becomes non-empty) and snap to the bottom — at
  // that moment scrollHeight reflects the real content.
  useEffect(() => {
    if (!pendingScrollToBottomRef.current) return;
    if (renderMessages.length === 0) return;
    pendingScrollToBottomRef.current = false;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    pinnedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMessages]);

  const onScroll = () => {
    const atBottom = isAtBottom();
    pinnedRef.current = atBottom;
    if (atBottom && unread !== 0) setUnread(0);
  };

  // Explicit jump-to-turn from outside (cited turn id click in GapLedger).
  // We depend on the whole object so a new {turnId, nonce} re-fires even when
  // turnId is unchanged — consecutive clicks on the same id still scroll.
  // Smooth here is intentional: the user asked for it explicitly.
  useEffect(() => {
    if (!jumpToTurn) return;
    const root = scrollRef.current;
    if (!root) return;
    const target = root.querySelector(`[data-turn-id="${CSS.escape(jumpToTurn.turnId)}"]`);
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      // Suppress auto-tail until the user manually scrolls down again.
      pinnedRef.current = false;
    }
  }, [jumpToTurn]);

  return (
    <section
      style={{
        // When `width` is provided, the parent (App) drives sizing via the
        // ColumnResizer and we render as a fixed-width column that doesn't
        // shrink. The right border is owned by the ColumnResizer in that
        // mode, so don't draw it here. Without `width`, fall back to the
        // original elastic 540–760 behavior so this component is usable
        // outside the App-level layout.
        ...(width != null
          ? { width, flexShrink: 0 }
          : {
              flex: '1 1 760px',
              minWidth: 540,
              maxWidth: 760,
              borderRight: '1px solid var(--rule)',
            }),
        background: 'var(--paper-0)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: 45,
          padding: '0 18px',
          borderBottom: '1px solid var(--rule)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <span style={{ color: 'var(--blue)', fontSize: 14 }}>✦</span>
        <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-0)', letterSpacing: '.1em', fontWeight: 700 }}>
          {t('discussion.headerTitle')}
        </div>
        {usage && <TokenUsageChip usage={usage} />}
        {modelChoices && onChangeModelPreference && (
          <TierPicker
            currentTier={threadModel ?? null}
            currentEffort={threadEffort ?? null}
            currentFastMode={Boolean(threadFastMode)}
            choices={modelChoices}
            hadAttachment={Boolean(threadHadAttachment)}
            onChange={onChangeModelPreference}
          />
        )}
        <span style={{ flex: 1 }} />
        {onDistill && messages.length > 0 && (
          <button
            onClick={() => onDistill()}
            disabled={distilling || busy}
            className="mono"
            title={t('discussion.distillTooltip')}
            style={{
              fontSize: 10,
              padding: '3px 8px',
              border: '1px solid var(--rule)',
              borderRadius: 3,
              color: distilling ? 'var(--ink-3)' : 'var(--ink-1)',
              background: 'white',
              cursor: distilling || busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.5 : 1,
              letterSpacing: '.08em',
            }}
          >
            {distilling ? t('discussion.distilling') : t('discussion.distillButton')}
          </button>
        )}
        <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', marginLeft: 8 }}>
          {t('discussion.agentDecidesHint')}
        </span>
      </div>

      {pinnedGap && (
        <PinnedGapBanner
          gap={pinnedGap}
          onResolve={onResolveGap}
          onApprove={onApprovePinnedGap}
          onReject={onRejectPinnedGap}
        />
      )}
      {!pinnedGap && postResolveAuditFor && (
        <PostResolveAuditBanner
          gapId={postResolveAuditFor}
          onAudit={onPostResolveAudit}
          onDismiss={onDismissPostResolveAudit}
        />
      )}

      <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 24px' }}>
        {messages.length === 0 && (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: 'var(--ink-3)',
              fontSize: 13,
              fontStyle: 'italic',
              maxWidth: 480,
              margin: '0 auto',
            }}
          >
            <div className="serif" style={{ fontSize: 22, color: 'var(--ink-2)', marginBottom: 8, fontStyle: 'normal' }}>
              {t('discussion.emptyTitle')}
            </div>
            <div>
              {t('discussion.emptyBody')}
            </div>
          </div>
        )}

        {renderMessages.map((m, i) => {
          // The last message is "live" if busy is true and it's an assistant
          // bubble. Stream deltas append to it; we render a blinking caret at
          // the tail to signal more is coming.
          const isLiveAssistant =
            busy && i === renderMessages.length - 1 && m.kind === 'assistant';
          const turnId = m.kind === 'thinking' ? undefined : m.turnId;
          const highlighted =
            !!turnId && !!highlightTurnIds && highlightTurnIds.has(turnId);
          const bulkIds = bulkPendingByMessageIdx.get(i);
          // If this propose_card belongs to a collapsed burst, hide it —
          // the BulkProposalBlock under the owning assistant bubble will
          // render its summary instead. When the user clicks [expand] on
          // the block, this branch becomes false and the row reappears.
          const ownerIdx = burstOwnerByMessageIdx.get(i);
          if (ownerIdx !== undefined && !expandedBursts.has(ownerIdx)) {
            return <Fragment key={i} />;
          }
          return (
            <Fragment key={i}>
              <Message
                msg={m}
                live={isLiveAssistant}
                highlighted={highlighted}
                onConvertToGaps={onConvertToGaps}
                convertingToGaps={!!convertingToGaps}
                cards={cards}
                onJumpToCard={onJumpToCard}
                knownIds={knownIds}
                onSelectCard={onSelectCard}
                onApproveProposal={onApproveProposal}
                onRejectProposal={onRejectProposal}
                onDiscussProposal={onDiscussProposal}
              />
              {bulkIds && onApproveProposal && (
                <BulkProposalBlock
                  cardIds={bulkIds}
                  cards={cards ?? []}
                  expanded={expandedBursts.has(i)}
                  onToggleExpand={() => toggleBurstExpanded(i)}
                  onApprove={onApproveProposal}
                  onApproveBatch={onApproveProposalBatch}
                  onReject={onRejectProposal}
                />
              )}
            </Fragment>
          );
        })}

        {busy && shouldShowConnectingPlaceholder(messages) && (
          <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', letterSpacing: '.08em', padding: '4px 0' }}>
            {(() => {
              // Special-case python_compute: the agent has emitted the
              // tool_use, control is now waiting on a Fly sandbox VM to
              // boot + run + return — ~5-8s typical. Without distinct
              // copy users assume "thinking" means the model is stuck.
              const last = messages[messages.length - 1];
              if (last?.kind === 'tool_call' && last.tool === 'python_compute') {
                return (
                  <>
                    {t('discussion.sandboxRunning')}<span className="caret" />
                  </>
                );
              }
              return (
                <>
                  {t('discussion.thinkingPlaceholder')}<span className="caret" />
                </>
              );
            })()}
          </div>
        )}
        </div>
      </div>
      {unread > 0 && (
        <button
          onClick={() => jumpToBottom(true)}
          className="mono"
          title={t('discussion.jumpToLatestTooltip')}
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 14,
            transform: 'translateX(-50%)',
            padding: '5px 12px',
            border: '1px solid var(--ink-0)',
            borderRadius: 999,
            background: 'var(--ink-0)',
            color: 'white',
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: '.05em',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
          }}
        >
          {unread === 1
            ? t('discussion.newReplyOne', { count: unread })
            : t('discussion.newReplyMany', { count: unread })}
        </button>
      )}
      </div>

      {/* Composer band. min-height + border-box pin the IDLE outer height
          to exactly 48 — the canonical bottom-band height also used by
          Sidebar SETTINGS / CardsPanel footer / GapLedger tips. The
          inner DraftInput is centered vertically so its rounded frame
          sits in the middle of the band; when the textarea grows
          multi-line, the wrapper grows past 48 (only THIS column's
          band lifts; the other three stay anchored at page-bottom-48). */}
      <div
        style={{
          borderTop: '1px solid var(--rule)',
          background: 'white',
          minHeight: 48,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            maxWidth: 760,
            margin: '0 auto',
            padding: '0 24px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <DraftInput
            onSend={onSend}
            busy={busy}
            onStopTurn={onStopTurn ?? null}
            projectId={projectId}
            queuedSend={queuedSend ?? null}
            onCancelQueuedSend={onCancelQueuedSend}
          />
        </div>
      </div>
    </section>
  );
}

// TokenUsageChip + UsageRow extracted to ./TokenUsageChip.tsx so the
// mobile chat can reuse the threshold maths and bottom-sheet variant.

/**
 * PR-57: per-thread tier picker — three sections (Models / Effort /
 * Fast mode) modelled on Claude Code's settings popover. Click the
 * chip → popover. Each section writes through to the same PATCH
 * endpoint on the server; the section maps to one field of the
 * preference payload (`model` / `effort` / `fast_mode`).
 *
 * Tier resolution: `currentTier` is the stored tier id (e.g.
 * "flash-1m") or NULL → server default. We render only the tiers the
 * server returned in `choices.tiers`; legacy raw model ids stored on
 * old threads still resolve at the server but display as the raw
 * value here since they don't appear in `tiers`.
 *
 * Effort section: hidden when the active tier has thinking_kind ==
 * "none" or "on_off" (only DeepSeek-style effort tiers expose graded
 * levels). The buttons render the tier's `effort_levels` exactly.
 *
 * Fast mode toggle: applies on top of any tier, forces thinking off
 * via `thinking: { type: "disabled" }` on the wire. While fast mode
 * is on, we still let the user pick effort but the server ignores
 * effort at request time (mutual exclusion enforced server-side).
 *
 * Vision sticky disclosure carries over from PR-56: when the active
 * thread has had_attachment=true the server forces a vision-capable
 * model regardless of selection. The popover footer explains this.
 */
function TierPicker({
  currentTier,
  currentEffort,
  currentFastMode,
  choices,
  hadAttachment,
  onChange,
}: {
  currentTier: string | null;
  currentEffort: string | null;
  currentFastMode: boolean;
  choices: ModelChoices;
  hadAttachment: boolean;
  onChange: (patch: {
    model?: string | null;
    effort?: string | null;
    fast_mode?: boolean;
  }) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Resolve the active tier from the server's tier list. When the
  // stored value is NULL we use the server default tier; when it's a
  // raw model id (legacy thread or env override) we won't find a
  // match here and fall back to a synthetic "Custom" view so the chip
  // still renders something readable.
  const effectiveTierId = currentTier ?? choices.default_tier;
  const activeTier =
    choices.tiers.find((t) => t.id === effectiveTierId) ?? null;
  const isCustom = activeTier == null && currentTier != null;
  const chipLabel = activeTier
    ? activeTier.display_name
    : currentTier
      ? currentTier
      : choices.default;
  const isDefault = currentTier == null;

  // Effort to render: server-provided tier default if no override. We
  // surface the *effective* effort (what the request will actually
  // send) so the chip preview matches what the server will do.
  const effectiveEffort =
    currentEffort ?? activeTier?.default_effort ?? null;
  const showEffortSection =
    activeTier != null && activeTier.thinking_kind === 'effort';

  // Compact chip subtitle: effort marker + fast mode dot. We keep
  // the chip narrow — full descriptions live in the popover.
  const chipSuffix: string[] = [];
  if (showEffortSection && effectiveEffort) {
    chipSuffix.push(effectiveEffort);
  }
  if (currentFastMode) {
    chipSuffix.push('fast');
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', marginLeft: 6 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={
          hadAttachment
            ? t('discussion.tierVisionStickyTooltip')
            : t('discussion.tierChipTooltip', {
                tier: chipLabel,
                suffix: chipSuffix.length ? ` (${chipSuffix.join(' · ')})` : '',
              })
        }
        className="mono"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          padding: '2px 7px',
          fontSize: 10,
          color: 'var(--ink-1)',
          background: open ? 'var(--paper-1, #fafaf6)' : 'transparent',
          border: '1px solid var(--rule)',
          borderRadius: 10,
          cursor: 'pointer',
          letterSpacing: '.04em',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {hadAttachment && <span aria-hidden>👁</span>}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {chipLabel}
        </span>
        {chipSuffix.length > 0 && (
          <span
            className="mono"
            style={{
              fontSize: 9,
              color: 'var(--ink-3)',
              letterSpacing: '.06em',
            }}
          >
            {chipSuffix.join('·')}
          </span>
        )}
        <span aria-hidden style={{ color: 'var(--ink-3)', fontSize: 8 }}>
          ▾
        </span>
      </button>
      {open && (
        <div
          role="dialog"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 10,
            background: 'white',
            border: '1px solid var(--rule)',
            borderRadius: 6,
            boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
            padding: 8,
            minWidth: 240,
          }}
        >
          {/* ── Models ───────────────────────────────────────── */}
          <div
            className="mono"
            style={{
              fontSize: 9.5,
              color: 'var(--ink-3)',
              letterSpacing: '.1em',
              fontWeight: 700,
              padding: '4px 8px 6px',
            }}
          >
            {t('discussion.tierModelsHeading')}
          </div>
          {choices.tiers.map((tier) => {
            const selected = effectiveTierId === tier.id;
            const isThisDefault = tier.id === choices.default_tier;
            return (
              <button
                key={tier.id}
                type="button"
                onClick={() => {
                  // Selecting the default tier writes NULL (clear
                  // override); other tiers store their id. Effort is
                  // reset alongside so the new tier's default_effort
                  // applies — otherwise switching from "Pro 1M / max"
                  // to "Pro 200K" would carry "max" forward and the
                  // server would silently drop it (GLM has no graded
                  // effort), which is confusing.
                  onChange({
                    model: isThisDefault ? null : tier.id,
                    effort: null,
                  });
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  width: '100%',
                  padding: '6px 8px',
                  background: selected ? 'var(--paper-1, #fafaf6)' : 'transparent',
                  border: 'none',
                  borderRadius: 3,
                  cursor: 'pointer',
                  fontSize: 12,
                  color: 'var(--ink-1)',
                  textAlign: 'left',
                  gap: 6,
                }}
                onMouseEnter={(e) => {
                  if (!selected) e.currentTarget.style.background = 'var(--paper-1, #fafaf6)';
                }}
                onMouseLeave={(e) => {
                  if (!selected) e.currentTarget.style.background = 'transparent';
                }}
              >
                <span
                  style={{
                    width: 14,
                    color: 'var(--ink-2)',
                    fontSize: 11,
                    flexShrink: 0,
                    paddingTop: 1,
                  }}
                >
                  {selected ? '✓' : ''}
                </span>
                <span style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="mono">{tier.display_name}</span>
                    {isThisDefault && (
                      <span
                        className="mono"
                        style={{
                          fontSize: 9,
                          color: 'var(--ink-3)',
                          letterSpacing: '.06em',
                        }}
                      >
                        {t('discussion.tierDefaultBadge')}
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 1 }}>
                    {tier.description}
                  </span>
                </span>
              </button>
            );
          })}
          {isCustom && (
            <div
              style={{
                fontSize: 10,
                color: 'var(--ink-3)',
                padding: '4px 8px',
                marginTop: 2,
                fontStyle: 'italic',
              }}
            >
              {t('discussion.tierCustomLabel')} <span className="mono">{currentTier}</span>
            </div>
          )}

          {/* ── Effort (only for tiers with graded thinking) ── */}
          {showEffortSection && (
            <>
              <div
                style={{
                  height: 1,
                  background: 'var(--rule)',
                  margin: '6px 0',
                }}
              />
              <div
                className="mono"
                style={{
                  fontSize: 9.5,
                  color: 'var(--ink-3)',
                  letterSpacing: '.1em',
                  fontWeight: 700,
                  padding: '4px 8px 6px',
                }}
              >
                {t('discussion.tierEffortHeading')}
              </div>
              {activeTier!.effort_levels.map((level) => {
                const selected = effectiveEffort === level;
                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => {
                      onChange({ effort: level });
                      setOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '6px 8px',
                      background: selected ? 'var(--paper-1, #fafaf6)' : 'transparent',
                      border: 'none',
                      borderRadius: 3,
                      cursor: 'pointer',
                      fontSize: 12,
                      color: currentFastMode ? 'var(--ink-3)' : 'var(--ink-1)',
                      textAlign: 'left',
                      gap: 6,
                    }}
                    onMouseEnter={(e) => {
                      if (!selected) e.currentTarget.style.background = 'var(--paper-1, #fafaf6)';
                    }}
                    onMouseLeave={(e) => {
                      if (!selected) e.currentTarget.style.background = 'transparent';
                    }}
                    title={
                      currentFastMode
                        ? t('discussion.tierEffortIgnoredTooltip')
                        : undefined
                    }
                  >
                    <span
                      style={{
                        width: 14,
                        color: 'var(--ink-2)',
                        fontSize: 11,
                        flexShrink: 0,
                      }}
                    >
                      {selected ? '✓' : ''}
                    </span>
                    <span className="mono" style={{ flex: 1, textTransform: 'capitalize' }}>
                      {level}
                    </span>
                  </button>
                );
              })}
            </>
          )}

          {/* ── Fast mode toggle ─────────────────────────────── */}
          <div
            style={{
              height: 1,
              background: 'var(--rule)',
              margin: '6px 0',
            }}
          />
          <button
            type="button"
            onClick={() => onChange({ fast_mode: !currentFastMode })}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              padding: '6px 8px',
              background: 'transparent',
              border: 'none',
              borderRadius: 3,
              cursor: 'pointer',
              fontSize: 12,
              color: 'var(--ink-1)',
              textAlign: 'left',
              gap: 6,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--paper-1, #fafaf6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            title={t('discussion.tierFastModeTooltip')}
          >
            <span style={{ width: 14, fontSize: 11, flexShrink: 0 }} />
            <span className="mono" style={{ flex: 1 }}>
              {t('discussion.tierFastMode')}
            </span>
            <span
              aria-hidden
              style={{
                width: 22,
                height: 12,
                borderRadius: 6,
                background: currentFastMode ? 'var(--blue, #4a72d9)' : 'var(--rule)',
                position: 'relative',
                transition: 'background 120ms ease',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 1,
                  left: currentFastMode ? 11 : 1,
                  width: 10,
                  height: 10,
                  borderRadius: 5,
                  background: 'white',
                  transition: 'left 120ms ease',
                }}
              />
            </span>
          </button>

          {hadAttachment && (
            <div
              style={{
                fontSize: 10,
                color: 'var(--ink-3)',
                padding: '8px',
                marginTop: 4,
                borderTop: '1px solid var(--rule)',
                lineHeight: 1.5,
              }}
            >
              <span aria-hidden>👁</span> {t('discussion.tierVisionStickyNote')}
            </div>
          )}
          {!isDefault && (
            <button
              type="button"
              onClick={() => {
                onChange({ model: null, effort: null, fast_mode: false });
                setOpen(false);
              }}
              className="mono"
              style={{
                display: 'block',
                width: '100%',
                marginTop: 6,
                padding: '6px 8px',
                fontSize: 10,
                color: 'var(--ink-3)',
                background: 'transparent',
                border: '1px dashed var(--rule)',
                borderRadius: 3,
                cursor: 'pointer',
                letterSpacing: '.06em',
              }}
            >
              {t('discussion.tierResetToDefault')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Pinned-gap banner: sits between the header and the message stream while
 * the user is "discussing" a particular gap. The card title + body appear
 * here; behind the scenes, the next user message goes out with a
 * [discussing-gap c-X] hint so the agent stays focused on resolving it.
 *
 * Resolve clears the pin (the gap card itself is untouched — Approve /
 * Reject in the Gap Ledger is what consumes the gap).
 */
function PinnedGapBanner({
  gap,
  onResolve,
  onApprove,
  onReject,
}: {
  gap: Card;
  onResolve?: () => void;
  onApprove?: (cardId: string) => Promise<void> | void;
  onReject?: (cardId: string) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<null | 'approve' | 'reject'>(null);
  // Question pins (post-redesign: clicking [answer ↗] in OPEN QUESTIONS
  // pins a kind=question card here so the user can answer it). For these,
  // Approve/Reject don't apply — /promote and /reject_gap both demand
  // kind=gap server-side and would 400. Only Resolve (clear pin) makes
  // sense; leave the answer to the conversation itself.
  const isQuestionPin = gap.kind === 'question';
  const literalProposedKind =
    typeof gap.extra?.['proposed_kind'] === 'string' && (gap.extra['proposed_kind'] as string).length > 0
      ? (gap.extra['proposed_kind'] as string)
      : null;
  const gapKind =
    typeof gap.extra?.['gap_kind'] === 'string' ? (gap.extra['gap_kind'] as string) : null;
  // For question pins the "destination" is itself — show 'question'. For
  // gap pins, mirror the server's promote fallback so the user sees the
  // real destination kind.
  const proposedKind = isQuestionPin
    ? 'question'
    : (literalProposedKind ?? (gapKind === 'conflict' ? 'statement' : 'question'));
  const display =
    gap.title?.trim() ||
    gap.body.split('\n').find((l) => l.trim().length > 0)?.trim() ||
    (isQuestionPin ? t('discussion.untitledQuestion') : t('discussion.untitledGap'));
  const ambiguity =
    typeof gap.extra?.['ambiguity_reason'] === 'string'
      ? (gap.extra['ambiguity_reason'] as string)
      : null;

  const handleApprove = async () => {
    if (!onApprove || busy) return;
    setBusy('approve');
    try {
      await onApprove(gap.card_id);
    } finally {
      setBusy(null);
    }
  };
  const handleReject = async () => {
    if (!onReject || busy) return;
    setBusy('reject');
    try {
      await onReject(gap.card_id);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      style={{
        background: 'var(--amber-soft)',
        borderBottom: '1px solid var(--amber)',
        padding: '8px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
        opacity: busy ? 0.6 : 1,
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 9.5,
          color: 'var(--amber)',
          letterSpacing: '.08em',
          fontWeight: 700,
          background: 'white',
          padding: '2px 6px',
          borderRadius: 2,
          border: '1px solid var(--amber)',
          flexShrink: 0,
        }}
      >
        {isQuestionPin
          ? t('discussion.pinnedBadgeAnswering', { id: gap.card_id })
          : t('discussion.pinnedBadgeDiscussing', { id: gap.card_id })}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12.5,
            color: 'var(--ink-0)',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={display}
        >
          <span className="mono" style={{ color: 'var(--ink-3)', marginRight: 6 }}>
            ({proposedKind})
          </span>
          {display}
        </div>
        {ambiguity && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-2)',
              fontStyle: 'italic',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={ambiguity}
          >
            {t('discussion.pinnedWhyPrefix')} {ambiguity}
          </div>
        )}
      </div>
      {/* Approve/Reject only render for gap pins. For question pins the
          server endpoints would 400 (kind!=gap), and conceptually the
          user "approves" by writing an answer + the agent crystallizing
          it via write_card; the question itself is then archived from
          the OPEN QUESTIONS section. Resolve below works for both. */}
      {!isQuestionPin && onApprove && (
        <button
          onClick={handleApprove}
          disabled={busy !== null}
          className="mono"
          title={
            isQuestionPin
              ? t('discussion.pinnedApproveQuestionTooltip')
              : t('discussion.pinnedApproveTooltip', { kind: proposedKind })
          }
          style={{
            fontSize: 10.5,
            padding: '3px 8px',
            border: '1px solid var(--ink-0)',
            borderRadius: 3,
            background: 'var(--ink-0)',
            color: 'white',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: busy === null ? 'pointer' : 'not-allowed',
            flexShrink: 0,
          }}
        >
          {busy === 'approve'
            ? '…'
            : proposedKind === 'question'
              ? t('discussion.addToOpen')
              : t('discussion.addToCards')}
        </button>
      )}
      {!isQuestionPin && onReject && (
        <button
          onClick={handleReject}
          disabled={busy !== null}
          className="mono"
          title={t('discussion.pinnedRejectTooltip')}
          style={{
            fontSize: 10.5,
            padding: '3px 8px',
            border: '1px solid var(--red)',
            borderRadius: 3,
            background: 'white',
            color: 'var(--red)',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: busy === null ? 'pointer' : 'not-allowed',
            flexShrink: 0,
          }}
        >
          {busy === 'reject' ? '…' : t('discussion.dismiss')}
        </button>
      )}
      {onResolve && (
        <button
          onClick={onResolve}
          disabled={busy !== null}
          className="mono"
          title={t('discussion.pinnedResolveTooltip')}
          style={{
            fontSize: 10.5,
            padding: '3px 8px',
            border: '1px solid var(--amber)',
            borderRadius: 3,
            background: 'white',
            color: 'var(--amber)',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: busy === null ? 'pointer' : 'not-allowed',
            flexShrink: 0,
          }}
        >
          {t('discussion.resolve')}
        </button>
      )}
    </div>
  );
}

// Memoized: chat history can grow long with markdown-heavy assistant
// turns. Without memo, every keystroke in DraftInput re-parses every
// ReactMarkdown subtree because DiscussionPane re-renders. With memo,
// shallow-compare on `msg` (stable object refs) + `live` (bool) skips
// all old messages, leaving only the streaming bubble to re-render.
const Message = memo(function Message({
  msg,
  live,
  highlighted,
  onConvertToGaps,
  convertingToGaps,
  cards,
  onJumpToCard,
  knownIds,
  onSelectCard,
  onApproveProposal,
  onRejectProposal,
  onDiscussProposal,
}: {
  msg: ChatMessage;
  live?: boolean;
  /** When true, the bubble's content gets a soft amber outline and the
   *  outer wrapper carries `data-turn-highlight="true"` so the parent
   *  scroll effect can find the first match and scroll it into view. */
  highlighted?: boolean;
  /** When provided AND the assistant message looks like a structured
   *  listicle (audit/decomposition/breakdown), render an inline button
   *  on the bubble that hands the turnId back to the parent for a
   *  scoped decompose-distill run. */
  onConvertToGaps?: (turnId: string) => void;
  convertingToGaps?: boolean;
  /** Forwarded to `ToolCallBadge` so a `propose_card` row can resolve
   *  which card it produced and let the user jump to it. */
  cards?: Card[];
  onJumpToCard?: (cardId: string, anchor: DOMRect) => void;
  /** Project-wide known card ids — drives wikilink rendering inside the
   *  assistant bubble. */
  knownIds?: ReadonlySet<string>;
  /** Click handler for `[[c-X]]` wikilinks inside the assistant bubble.
   *  Just selects the target (cards panel scrolls into view); no peek. */
  onSelectCard?: (id: string) => void;
  /** Forwarded to `ToolCallBadge`. When the row is a `propose_card` whose
   *  target is still kind=gap, the badge renders an inline action row
   *  routing through these. */
  onApproveProposal?: (cardId: string) => Promise<void> | void;
  onRejectProposal?: (cardId: string) => Promise<void> | void;
  onDiscussProposal?: (cardId: string) => void;
}) {
  const { t } = useTranslation();
  // The amber ring + smooth transition. Applied to the bubble content node
  // (not the outer wrapper) so the role label above stays unaffected.
  const ringStyle: React.CSSProperties = highlighted
    ? {
        boxShadow: '0 0 0 2px var(--amber, #d4a017)',
        transition: 'box-shadow 160ms ease-out',
      }
    : { transition: 'box-shadow 160ms ease-out' };
  // `data-turn-id` is set whenever we know which session message this bubble
  // came from — drives the "click cited turn id → jump" effect in the
  // parent. `data-turn-highlight` is the transient hover marker; it can
  // coexist on the same element.
  const turnId = msg.kind === 'thinking' ? undefined : msg.turnId;
  const wrapperProps = {
    ...(turnId ? { 'data-turn-id': turnId } : {}),
    ...(highlighted ? { 'data-turn-highlight': 'true' as const } : {}),
  };

  if (msg.kind === 'user') {
    return (
      <div
        {...wrapperProps}
        style={{ marginBottom: 14, maxWidth: '88%', marginLeft: 'auto' }}
      >
        <div
          className="mono"
          style={{
            fontSize: 9, color: 'var(--ink-3)',
            letterSpacing: '.1em', marginBottom: 3, textAlign: 'right',
          }}
        >
          {t('discussion.roleYou', { time: fmt(msg.ts) })}
        </div>
        <div
          style={{
            background: 'var(--ink-0)', color: 'white',
            padding: '9px 13px', borderRadius: '12px 12px 4px 12px',
            fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
            ...ringStyle,
          }}
        >
          {msg.text}
        </div>
      </div>
    );
  }
  if (msg.kind === 'assistant') {
    // Listicle detection: only run when the bubble is settled (not live)
    // and the parent wired the convert affordance. We score the body for
    // "structured artifact" features and surface the button past a
    // threshold. Live bubbles don't qualify because the text is still
    // being streamed in — the score would oscillate.
    const listicleScore =
      !live && onConvertToGaps && msg.turnId
        ? scoreListicle(msg.text)
        : null;
    const showConvert = listicleScore !== null && listicleScore.qualifies;
    return (
      <div {...wrapperProps} style={{ marginBottom: 14, maxWidth: '92%' }}>
        <div className="mono" style={{ fontSize: 9, color: 'var(--ink-3)', letterSpacing: '.1em', marginBottom: 3 }}>
          {t('discussion.roleAgent', { time: fmt(msg.ts) })}
        </div>
        <div
          className="md-chat"
          style={{
            background: 'white', color: 'var(--ink-0)',
            padding: '9px 13px', borderRadius: '12px 12px 12px 4px',
            border: '1px solid var(--rule)',
            fontSize: 13, lineHeight: 1.6,
            ...ringStyle,
          }}
        >
          {/* CardBodyMarkdown does the same remark-gfm rendering as plain
              ReactMarkdown but additionally turns `[[c-X]]` mentions whose
              id is in `knownIds` into clickable jump links. Click routes
              through `onJumpToCard` (selects + flashes + opens peek
              anchored to the link rect — CardPeek falls back to centered
              when there isn't room to the left). Falls back to plain
              `onSelectCard` when no peek handler is wired. Broken refs
              (id not in knownIds) stay as literal text either way. */}
          <CardBodyMarkdown
            body={msg.text}
            knownIds={knownIds ?? EMPTY_ID_SET}
            onJumpToCard={(id, anchor) => {
              if (onJumpToCard && anchor) {
                onJumpToCard(id, anchor);
                return;
              }
              onSelectCard?.(id);
            }}
          />
          {live && <span className="caret" />}
        </div>
        {showConvert && msg.turnId && (
          <button
            onClick={() => onConvertToGaps?.(msg.turnId!)}
            disabled={!!convertingToGaps}
            className="mono"
            title={t('discussion.convertTooltip')}
            style={{
              marginTop: 6,
              fontSize: 10,
              padding: '4px 10px',
              border: '1px solid #6366f1',
              borderRadius: 3,
              background: convertingToGaps ? '#e0e7ff' : 'white',
              color: convertingToGaps ? 'var(--ink-3)' : '#4f46e5',
              cursor: convertingToGaps ? 'wait' : 'pointer',
              letterSpacing: '.06em',
            }}
          >
            {convertingToGaps
              ? t('discussion.converting')
              : listicleScore!.itemCount === 1
                ? t('discussion.convertItemsOne', { count: listicleScore!.itemCount })
                : t('discussion.convertItemsMany', { count: listicleScore!.itemCount })}
          </button>
        )}
        {msg.truncated && (
          <div
            className="mono"
            title={t('discussion.truncatedTooltip')}
            style={{
              marginTop: 4,
              padding: '3px 8px',
              fontSize: 10,
              letterSpacing: '.06em',
              color: 'var(--amber, #8a5a00)',
              background: 'var(--amber-soft, #fff5d6)',
              border: '1px solid var(--amber-rule, #e6c879)',
              borderRadius: 3,
              display: 'inline-block',
            }}
          >
            {t('discussion.truncatedLabel')}
          </div>
        )}
      </div>
    );
  }
  if (msg.kind === 'thinking') {
    return <ThinkingBubble text={msg.text} done={msg.done} ts={msg.ts} />;
  }
  // tool_call — compact one-line badge with click-to-expand for raw input.
  // The agent's Approve/Discuss flows fire several tool calls per turn and
  // each result already lands in the cards / Gap Ledger panels live; the
  // chat doesn't need a full panel for every call.
  return (
    <div {...wrapperProps} style={{ marginBottom: 6, maxWidth: '92%' }}>
      <ToolCallBadge
        tool={msg.tool}
        input={msg.input}
        ts={msg.ts}
        highlighted={!!highlighted}
        cards={cards}
        onJumpToCard={onJumpToCard}
        onApproveProposal={onApproveProposal}
        onRejectProposal={onRejectProposal}
        onDiscussProposal={onDiscussProposal}
      />
    </div>
  );
});

function ToolCallBadge({
  tool,
  input,
  ts,
  highlighted,
  cards,
  onJumpToCard,
  onApproveProposal,
  onRejectProposal,
  onDiscussProposal,
}: {
  tool: string;
  input: string;
  ts: number;
  highlighted?: boolean;
  cards?: Card[];
  onJumpToCard?: (cardId: string, anchor: DOMRect) => void;
  /** Inline approve/discuss/reject for a `propose_card` row whose target
   *  is still kind=gap. When all three are absent (or the row isn't a
   *  pending proposal), the badge falls back to its prior click-to-jump
   *  / click-to-expand behavior. */
  onApproveProposal?: (cardId: string) => Promise<void> | void;
  onRejectProposal?: (cardId: string) => Promise<void> | void;
  onDiscussProposal?: (cardId: string) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState<null | 'approve' | 'reject' | 'discuss'>(null);
  // For propose_card / write_card / read_card / delete_card, attempt to
  // resolve the card the call is about. When found AND a jump handler is
  // wired, the row's primary click action becomes "scroll the cards panel
  // to that card and open its peek". Falls back to JSON expand otherwise
  // (the existing affordance — useful for tools without a card target, or
  // while the just-proposed card hasn't landed in `cards` yet).
  const resolvedId = onJumpToCard ? resolveCardForToolCall(tool, input, cards) : null;
  const resolvedCard = resolvedId
    ? (cards ?? []).find((c) => c.card_id === resolvedId) ?? null
    : null;
  const isPropose = tool === 'propose_card';
  const isPendingProposal =
    isPropose && resolvedCard?.kind === 'gap' && !!onApproveProposal;
  const isPromotedProposal =
    isPropose && resolvedCard !== null && resolvedCard.kind !== 'gap';
  // The kind this gap will become on Approve. Drives the button labels:
  //   destination 'question' → Add to OPEN  /  Answer now  /  Dismiss
  //   otherwise              → Add to cards /  Discuss     /  Dismiss
  // The split exists because question is conceptually different from the
  // other proposed kinds — it isn't a settled artifact going into the
  // cards column, it's a tracked open item joining OPEN's questions
  // section. Naming the destination on the button removes the "approve =
  // ?" question that earlier wording (`Approve`) caused.
  //
  // Mirror the server's promote fallback (card_ops::default_target_kind_
  // for_legacy_gap): legacy gaps with only `gap_kind` (no `proposed_kind`)
  // land as `statement` (gap_kind=conflict) or `question` (everything
  // else). Without this, the label would lie when proposed_kind is
  // missing — exactly the case dogfooded on c-12 where the button said
  // `Add to cards` but the card actually landed in OPEN QUESTIONS.
  const proposedKind: string = (() => {
    const extra = (resolvedCard?.extra ?? null) as Record<string, unknown> | null;
    if (extra) {
      const v = extra['proposed_kind'];
      if (typeof v === 'string' && v.length > 0) return v;
      const gk = extra['gap_kind'];
      if (typeof gk === 'string') {
        return gk === 'conflict' ? 'statement' : 'question';
      }
    }
    return 'question';
  })();
  const isQuestionProposal = proposedKind === 'question';
  // After approve, replace the summary with a "✓ approved → c-XX (kind)"
  // line so the user can see at a glance which proposal landed where.
  const summary = isPromotedProposal
    ? ` ${t('discussion.toolApprovedSummary', {
        id: resolvedCard!.card_id,
        kind: resolvedCard!.kind,
      })}`
    : summarizeToolCall(tool, input);
  const canJump = !!resolvedId;

  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!resolvedId || !onApproveProposal || busy) return;
    setBusy('approve');
    try {
      await onApproveProposal(resolvedId);
    } finally {
      setBusy(null);
    }
  };
  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!resolvedId || !onRejectProposal || busy) return;
    setBusy('reject');
    try {
      await onRejectProposal(resolvedId);
    } finally {
      setBusy(null);
    }
  };
  const handleDiscuss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!resolvedId || !onDiscussProposal || busy) return;
    setBusy('discuss');
    onDiscussProposal(resolvedId);
    // Discuss is fire-and-forget (it kicks off thread creation, not a
    // promise we await here). Clear the busy flag on the next tick so the
    // row visually settles even if the parent's state update is async.
    setTimeout(() => setBusy(null), 200);
  };

  return (
    <div
      className="mono"
      style={{
        fontSize: 11,
        color: 'var(--ink-2)',
        background: highlighted ? 'var(--amber-soft)' : 'transparent',
        borderLeft: `2px solid ${isPromotedProposal ? 'var(--green, #2f7a3a)' : 'var(--blue)'}`,
        padding: '3px 10px',
        lineHeight: 1.5,
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background 100ms',
      }}
      onClick={(e) => {
        if (canJump && resolvedId && onJumpToCard) {
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          onJumpToCard(resolvedId, rect);
          return;
        }
        setExpanded((v) => !v);
      }}
      title={canJump ? t('discussion.toolJumpTooltip', { id: resolvedId }) : t('discussion.toolToggleTooltip')}
    >
      <span style={{ color: 'var(--ink-3)', marginRight: 4 }}>
        {expanded ? '▾' : '▸'}
      </span>
      <span
        style={{
          color: isPromotedProposal ? 'var(--green, #2f7a3a)' : 'var(--blue)',
          fontWeight: 600,
        }}
      >
        {tool}
      </span>
      {summary && (
        <span style={{ color: 'var(--ink-2)', marginLeft: 6 }}>{summary}</span>
      )}
      {canJump && resolvedId && !isPromotedProposal && (
        <span
          className="mono"
          style={{
            color: 'var(--blue)',
            marginLeft: 6,
            fontSize: 10,
            letterSpacing: '.04em',
          }}
        >
          · {resolvedId} ↗
        </span>
      )}
      <span style={{ color: 'var(--ink-3)', marginLeft: 8, fontSize: 9.5 }}>
        {fmt(ts)}
      </span>
      {isPendingProposal && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            marginTop: 4,
            marginLeft: 14,
            opacity: busy ? 0.6 : 1,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleApprove}
            disabled={busy !== null}
            className="mono"
            title={
              isQuestionProposal
                ? t('discussion.toolApproveQuestionTooltip')
                : t('discussion.toolApproveTooltip', { kind: proposedKind })
            }
            style={{
              fontSize: 10,
              padding: '2px 8px',
              border: '1px solid var(--ink-0)',
              borderRadius: 3,
              background: 'var(--ink-0)',
              color: 'white',
              fontWeight: 600,
              letterSpacing: '.04em',
              cursor: busy === null ? 'pointer' : 'not-allowed',
            }}
          >
            {busy === 'approve'
              ? '…'
              : isQuestionProposal
                ? t('discussion.addToOpen')
                : t('discussion.addToCards')}
          </button>
          {onDiscussProposal && (
            <button
              onClick={handleDiscuss}
              disabled={busy !== null}
              className="mono"
              title={
                isQuestionProposal
                  ? t('discussion.toolDiscussQuestionTooltip')
                  : t('discussion.toolDiscussTooltip')
              }
              style={{
                fontSize: 10,
                padding: '2px 8px',
                border: '1px solid var(--amber)',
                borderRadius: 3,
                background: 'white',
                color: 'var(--amber)',
                fontWeight: 600,
                letterSpacing: '.04em',
                cursor: busy === null ? 'pointer' : 'not-allowed',
              }}
            >
              {busy === 'discuss'
                ? '…'
                : isQuestionProposal
                  ? t('discussion.answerNow')
                  : t('discussion.discuss')}
            </button>
          )}
          {onRejectProposal && (
            <button
              onClick={handleReject}
              disabled={busy !== null}
              className="mono"
              title={t('discussion.toolRejectTooltip')}
              style={{
                fontSize: 10,
                padding: '2px 8px',
                border: '1px solid var(--red)',
                borderRadius: 3,
                background: 'white',
                color: 'var(--red)',
                fontWeight: 600,
                letterSpacing: '.04em',
                cursor: busy === null ? 'pointer' : 'not-allowed',
              }}
            >
              {busy === 'reject' ? '…' : t('discussion.dismiss')}
            </button>
          )}
        </div>
      )}
      {expanded && (
        <pre
          style={{
            margin: '6px 0 4px 14px',
            padding: '6px 8px',
            background: 'var(--paper-1, #fafaf7)',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            fontSize: 10.5,
            lineHeight: 1.45,
            color: 'var(--ink-2)',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 320,
            overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {prettyJson(input)}
        </pre>
      )}
    </div>
  );
}

/**
 * Bulk approve/reject block for an agent burst that proposed ≥3 cards.
 * Renders directly under the assistant text bubble that wraps up the
 * burst. Two render modes (controlled by `expanded`):
 *
 *   - **collapsed (default)**: shows a compact summary card listing each
 *     proposal as one line (`→ question · "title"` / `⊳ decision · "…"`),
 *     plus three buttons:
 *        [Accept all (X→OPEN, Y→cards)]   ← smart label by proposed_kind mix
 *        [expand]                          ← reveal individual ToolCallBadges
 *        [Dismiss all]
 *     The owning render loop hides the propose_card ToolCallBadges in
 *     this mode (see `burstOwnerByMessageIdx` in the parent).
 *
 *   - **expanded**: the propose_card rows are visible again (each with
 *     its own [Add to OPEN/Add to cards] [Answer now/Discuss] [Dismiss]
 *     buttons), and this block shrinks to a thin `[collapse]` toggle so
 *     the user can hide them again.
 *
 * Approve goes through `onApproveBatch` (parallel + Undo toast). When
 * that prop is missing, falls back to sequential `onApprove` per id.
 */
function BulkProposalBlock({
  cardIds,
  cards,
  expanded,
  onToggleExpand,
  onApprove,
  onApproveBatch,
  onReject,
}: {
  cardIds: string[];
  /** Used to look up each proposal's title + extra.proposed_kind so the
   *  summary line can render `→ question · "<title>"` and the bulk
   *  button can compute its destination breakdown. */
  cards: Card[];
  expanded: boolean;
  onToggleExpand: () => void;
  onApprove: (cardId: string) => Promise<void> | void;
  onApproveBatch?: (
    cardIds: string[],
  ) => Promise<{ ok: string[]; failed: string[] }>;
  onReject?: (cardId: string) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<null | 'approve' | 'reject'>(null);
  const cardById = useMemo(() => new Map(cards.map((c) => [c.card_id, c])), [cards]);
  // Resolve each proposed card → its proposed_kind. Cards no longer in
  // the live list (deleted between render and now) drop to a defensive
  // 'card' default so the summary still renders something.
  const items = useMemo(
    () =>
      cardIds.map((id) => {
        const c = cardById.get(id);
        const extra = (c?.extra ?? null) as Record<string, unknown> | null;
        // Mirror server's default_target_kind_for_legacy_gap so the
        // summary line + bulk button label match where each card actually
        // lands. A legacy gap with `gap_kind=decision` (no `proposed_kind`)
        // promotes to `question`, not `decision` — the summary needs to
        // reflect that or the user's "X→cards Y→OPEN" preview is wrong.
        const literalPk = extra && typeof extra['proposed_kind'] === 'string' ? (extra['proposed_kind'] as string) : null;
        const gapKind = extra && typeof extra['gap_kind'] === 'string' ? (extra['gap_kind'] as string) : null;
        const pk =
          literalPk ?? (gapKind === 'conflict' ? 'statement' : 'question');
        const title =
          (c?.title?.trim() ||
            c?.body.split('\n').find((l) => l.trim().length > 0)?.trim() ||
            id);
        return { id, proposedKind: pk, title };
      }),
    [cardIds, cardById],
  );
  const toQuestion = items.filter((it) => it.proposedKind === 'question').length;
  const toCards = items.length - toQuestion;
  /** Smart bulk button label per spec §3 (DiscussionPane Inline Block):
   *    all question  → Add all to OPEN (N)
   *    all other     → Add all to cards (N)
   *    mixed         → Accept all (X→OPEN, Y→cards) */
  const bulkLabel = (() => {
    if (toQuestion === items.length)
      return t('discussion.bulkAddAllToOpen', { count: items.length });
    if (toCards === items.length)
      return t('discussion.bulkAddAllToCards', { count: items.length });
    const parts: string[] = [];
    if (toCards > 0) parts.push(t('discussion.bulkPartCards', { count: toCards }));
    if (toQuestion > 0) parts.push(t('discussion.bulkPartOpen', { count: toQuestion }));
    return t('discussion.bulkAcceptAll', { parts: parts.join(', ') });
  })();

  const handleApproveAll = async () => {
    if (busy) return;
    setBusy('approve');
    try {
      if (onApproveBatch) {
        await onApproveBatch(cardIds);
      } else {
        // Fallback: sequential, no undo. Older parents that haven't
        // wired the batch handler still get bulk-acceptance, just
        // without the [Undo] toast.
        for (const id of cardIds) {
          await onApprove(id);
        }
      }
    } finally {
      setBusy(null);
    }
  };
  const handleRejectAll = async () => {
    if (busy || !onReject) return;
    setBusy('reject');
    try {
      // Sequential to keep dismissed_gaps marks coherent (each reject
      // patches `extra.dismissed_gaps` on its blocks targets — server
      // serializes per card, but parallel patches on the same target
      // could race in theory). Cheap enough at typical burst sizes.
      for (const id of cardIds) {
        await onReject(id);
      }
    } finally {
      setBusy(null);
    }
  };

  // Expanded form: thin bar with just a [collapse] toggle. The individual
  // ToolCallBadge rows above carry their own per-row buttons.
  if (expanded) {
    return (
      <div
        style={{
          margin: '0 0 12px 14px',
          padding: '4px 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: '1px dashed var(--rule)',
          borderRadius: 4,
          background: 'var(--paper-1, #fafaf7)',
          maxWidth: '92%',
        }}
      >
        <span
          className="mono"
          style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.04em' }}
        >
          {t('discussion.bulkProposalsExpanded', { count: items.length })}
        </span>
        <span style={{ flex: 1 }} />
        <button
          onClick={onToggleExpand}
          className="mono"
          style={{
            fontSize: 10,
            padding: '2px 8px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            color: 'var(--ink-2)',
            cursor: 'pointer',
            letterSpacing: '.04em',
          }}
        >
          {t('discussion.collapse')}
        </button>
      </div>
    );
  }

  // Collapsed (default): the summary block.
  return (
    <div
      style={{
        margin: '0 0 12px 14px',
        padding: '8px 10px',
        border: '1px dashed var(--rule)',
        borderRadius: 4,
        background: 'var(--paper-1, #fafaf7)',
        maxWidth: '92%',
        opacity: busy ? 0.6 : 1,
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 10.5,
          color: 'var(--ink-2)',
          letterSpacing: '.04em',
          marginBottom: 6,
          fontWeight: 600,
        }}
      >
        💡 {t('discussion.bulkProposalsCount', { count: items.length })}
        {toQuestion > 0 && toCards > 0 && (
          <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>
            {' '}{t('discussion.bulkBreakdownMixed', { questions: toQuestion, other: toCards })}
          </span>
        )}
        {toQuestion === items.length && (
          <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}> {t('discussion.bulkBreakdownAllQuestions')}</span>
        )}
        {toCards === items.length && (
          <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}> {t('discussion.bulkBreakdownAllSettled')}</span>
        )}
      </div>
      <div style={{ marginBottom: 8 }}>
        {items.map((it) => (
          <div
            key={it.id}
            className="mono"
            style={{
              fontSize: 11,
              color: 'var(--ink-1)',
              padding: '2px 0',
              display: 'flex',
              gap: 8,
              alignItems: 'baseline',
              lineHeight: 1.4,
            }}
            title={it.id}
          >
            <span
              style={{
                color: it.proposedKind === 'question' ? 'var(--amber)' : 'var(--blue)',
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              {it.proposedKind === 'question' ? '→' : '⊳'} {it.proposedKind}
            </span>
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {it.title}
            </span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          onClick={handleApproveAll}
          disabled={busy !== null}
          className="mono"
          title={t('discussion.bulkApproveAllTooltip')}
          style={{
            fontSize: 10.5,
            padding: '3px 10px',
            border: '1px solid var(--ink-0)',
            borderRadius: 3,
            background: 'var(--ink-0)',
            color: 'white',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: busy === null ? 'pointer' : 'not-allowed',
          }}
        >
          {busy === 'approve' ? t('discussion.working') : bulkLabel}
        </button>
        <button
          onClick={onToggleExpand}
          disabled={busy !== null}
          className="mono"
          title={t('discussion.bulkExpandTooltip')}
          style={{
            fontSize: 10.5,
            padding: '3px 10px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            color: 'var(--ink-2)',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: busy === null ? 'pointer' : 'not-allowed',
          }}
        >
          {t('discussion.expand')}
        </button>
        <span style={{ flex: 1 }} />
        {onReject && (
          <button
            onClick={handleRejectAll}
            disabled={busy !== null}
            className="mono"
            title={t('discussion.bulkRejectAllTooltip')}
            style={{
              fontSize: 10.5,
              padding: '3px 10px',
              border: '1px solid var(--red)',
              borderRadius: 3,
              background: 'white',
              color: 'var(--red)',
              fontWeight: 600,
              letterSpacing: '.04em',
              cursor: busy === null ? 'pointer' : 'not-allowed',
            }}
          >
            {busy === 'reject' ? t('discussion.working') : t('discussion.dismissAll')}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Map a tool-call to the card it operates on, if any. Used so clicking a
 * `propose_card` / `write_card` / `read_card` / `delete_card` row jumps to
 * that card. Returns null when no match is possible — the click then falls
 * back to the JSON-expand affordance.
 *
 * `propose_card` doesn't carry an id (server-allocated); we match by the
 * gap kind it produces — kind=gap, extra.proposed_kind == input.proposed_kind,
 * title == input.title. Falls back to "any card with this title" so an
 * already-approved gap (now its proposed_kind, gap_kind stripped) still
 * resolves. Most-recent-first when multiple match.
 *
 * `write_card` carries `id` when updating an existing card. When `id` is
 * absent (newly written), we fall back to title match the same way.
 */
function resolveCardForToolCall(
  tool: string,
  input: string,
  cards: Card[] | undefined,
): string | null {
  if (!cards || cards.length === 0) return null;
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(input) as Record<string, unknown>;
  } catch {
    return null;
  }
  const str = (k: string): string | null => {
    const v = parsed[k];
    return typeof v === 'string' ? v : null;
  };
  const knownId = str('id');
  if (knownId && cards.some((c) => c.card_id === knownId)) {
    return knownId;
  }
  if (tool === 'read_card' || tool === 'delete_card') {
    return null; // these are id-only; nothing else to match on
  }
  if (tool !== 'propose_card' && tool !== 'write_card') {
    return null;
  }
  const wantTitle = str('title')?.trim() ?? '';
  if (!wantTitle) return null;
  const wantKind = tool === 'propose_card' ? str('proposed_kind') : str('kind');

  const titleMatches = cards.filter((c) => (c.title ?? '').trim() === wantTitle);
  if (titleMatches.length === 0) return null;

  // Prefer the freshest matching card. For propose_card, prefer ones still
  // in the gap state (kind=gap + matching proposed_kind) — that's the row
  // the user just saw the agent generate.
  const sorted = [...titleMatches].sort((a, b) => mtimeDesc(a, b));
  if (tool === 'propose_card' && wantKind) {
    const gap = sorted.find(
      (c) => c.kind === 'gap' && stringExtra(c.extra, 'proposed_kind') === wantKind,
    );
    if (gap) return gap.card_id;
    const promoted = sorted.find((c) => c.kind === wantKind);
    if (promoted) return promoted.card_id;
  } else if (tool === 'write_card' && wantKind) {
    const k = sorted.find((c) => c.kind === wantKind);
    if (k) return k.card_id;
  }
  return sorted[0].card_id;
}

function mtimeDesc(a: Card, b: Card): number {
  // Most recent first. mtime is an ISO timestamp string; fall back to
  // updated_at / created_at when older snapshots lack one.
  const at = a.mtime || a.updated_at || a.created_at || '';
  const bt = b.mtime || b.updated_at || b.created_at || '';
  return bt.localeCompare(at);
}

function stringExtra(extra: Record<string, unknown>, key: string): string | null {
  const v = extra[key];
  return typeof v === 'string' ? v : null;
}

/**
 * Build a one-line summary of a tool call from the raw JSON input. The
 * goal is "what did the agent do" at a glance — full input is one click
 * away. Falls back to a short truncation when we don't have a custom
 * formatter for the tool (or when JSON parse fails).
 */
function summarizeToolCall(tool: string, input: string): string {
  let parsed: Record<string, unknown> = {};
  try {
    parsed = JSON.parse(input) as Record<string, unknown>;
  } catch {
    return ` · ${truncate(input, 60)}`;
  }
  const str = (k: string): string | null => {
    const v = parsed[k];
    return typeof v === 'string' ? v : null;
  };
  const arr = (k: string): string[] | null => {
    const v = parsed[k];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null;
  };
  const trimTitle = (s: string | null) => (s ? truncate(s.replace(/\n/g, ' '), 50) : '');

  switch (tool) {
    case 'propose_card': {
      const kind = str('proposed_kind') ?? '?';
      const title = trimTitle(str('title')) || trimTitle(str('body'));
      const blocks = arr('blocks');
      const blocksStr = blocks && blocks.length > 0 ? ` · blocks=${blocks.join(',')}` : '';
      return ` → ${kind} · "${title}"${blocksStr}`;
    }
    case 'write_card': {
      const kind = str('kind') ?? '?';
      const id = str('id');
      const title = trimTitle(str('title')) || trimTitle(str('body'));
      return ` ${id ? `${id} ` : ''}(${kind}) "${title}"`;
    }
    case 'read_card':
    case 'delete_card':
      return ` ${str('id') ?? ''}`;
    case 'list_cards': {
      const k = str('kind');
      return k ? ` kind=${k}` : '';
    }
    case 'search_cards': {
      const q = str('query');
      const k = str('kind');
      return ` "${trimTitle(q)}"${k ? ` kind=${k}` : ''}`;
    }
    case 'link_cards':
      return ` ${str('source_id') ?? '?'} →${str('edge_type') ?? '?'} ${str('target_id') ?? '?'}`;
    case 'write_memory':
      return ` (${(str('text') ?? '').length} chars)`;
    case 'read_memory':
    case 'list_refs':
      return '';
    case 'read_ref':
      return ` ${str('name') ?? ''}`;
    case 'web_search': {
      const q = str('query');
      const allow = arr('allowed_domains');
      const block = arr('blocked_domains');
      const filter = allow && allow.length
        ? ` · only=${allow.join(',')}`
        : block && block.length
          ? ` · skip=${block.join(',')}`
          : '';
      return ` "${trimTitle(q)}"${filter}`;
    }
    case 'web_fetch': {
      const url = str('url') ?? '';
      const prompt = trimTitle(str('prompt'));
      return ` ${truncate(url, 60)}${prompt ? ` · "${prompt}"` : ''}`;
    }
    default:
      return ` · ${truncate(input, 60)}`;
  }
}

function prettyJson(input: string): string {
  try {
    return JSON.stringify(JSON.parse(input), null, 2);
  } catch {
    return input;
  }
}

/// Live-streaming reasoning bubble. While `done=false` (model still
/// thinking) the full text streams in dimmed mono with a caret and a
/// max-height scroll window. On `done=true` the bubble collapses to a
/// "▾ reasoning (N chars)" toggle; click reveals the captured text.
function ThinkingBubble({ text, done, ts }: { text: string; done: boolean; ts: number }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(!done);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll the reasoning view to the tail as new chunks arrive,
  // but only while it's still streaming (don't fight the user once they
  // start scrolling through the captured reasoning).
  useEffect(() => {
    if (!done && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [text, done]);

  // Once thinking ends, collapse by default — user can re-expand on click.
  useEffect(() => {
    if (done) setExpanded(false);
  }, [done]);

  const charCount = text.length;

  return (
    <div style={{ marginBottom: 10, maxWidth: '92%' }}>
      <div
        className="mono"
        onClick={done ? () => setExpanded((v) => !v) : undefined}
        style={{
          fontSize: 9,
          color: 'var(--ink-3)',
          letterSpacing: '.1em',
          marginBottom: 3,
          cursor: done ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        {t('discussion.thinkingLabel', { time: fmt(ts) })}
        {done && (
          <span style={{ marginLeft: 6 }}>
            {expanded ? '▾' : '▸'} {t('discussion.thinkingChars', { count: charCount })}
          </span>
        )}
      </div>
      {expanded && (
        <div
          ref={scrollRef}
          style={{
            background: 'var(--paper-1, #fafaf7)',
            borderLeft: '2px solid var(--ink-4, #ddd)',
            padding: '6px 10px',
            fontSize: 11.5,
            lineHeight: 1.55,
            color: 'var(--ink-2)',
            fontStyle: 'italic',
            whiteSpace: 'pre-wrap',
            maxHeight: done ? 240 : 140,
            overflowY: 'auto',
          }}
        >
          {text}
          {!done && <span className="caret" />}
        </div>
      )}
    </div>
  );
}

// Owns its own `draft` state so keystrokes don't re-render the DiscussionPane
// (and therefore don't re-render the messages list / re-parse markdown).
//
// Drag-drop: dropping any file uploads it as a project ref (`_refs/`) and
// surfaces a chip above the textarea. On send, attached filenames are
// prepended as a `attached: …` line so the agent knows to pull them via
// `read_ref` if relevant. The chip's × removes the auto-mention only —
// the uploaded file itself stays in the project; manage from the 📎 popover.
function DraftInput({
  onSend,
  busy,
  onStopTurn,
  projectId,
  queuedSend,
  onCancelQueuedSend,
}: {
  onSend: (text: string, options?: { imageAttachments?: string[] }) => void;
  busy: boolean;
  /** When busy + this is set, the right-hand composer button switches
   *  from "queue" to "stop ■" and click trips the cancel token on the
   *  in-flight turn. Null while a turn_id hasn't been captured yet — in
   *  that ~10ms gap, the button falls back to "queue" (no stop target
   *  yet, but the turn finishes too fast to matter). */
  onStopTurn: (() => void) | null;
  projectId: string | null;
  /** Active thread's queued send (if any). Renders as a chip above the
   *  textarea so the user can see — and cancel — what's in flight after
   *  the current turn ends. Owned by App.tsx; this component just shows it. */
  queuedSend: { text: string; imageRefs: string[] } | null;
  onCancelQueuedSend?: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [attached, setAttached] = useState<string[]>([]);
  const [uploading, setUploading] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [showRefsPopover, setShowRefsPopover] = useState(false);
  const [focused, setFocused] = useState(false);
  const dragDepth = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Auto-grow is handled by CSS `field-sizing: content` on the textarea
  // (browsers shipped 2024). No JS measurement / no scrollHeight reads /
  // no flex layout entanglement — the previous JS approach blew up to
  // 200+px in some flex parents because scrollHeight reflected the
  // parent's available space rather than content size.

  // Discord-style auto-focus: pressing a printable key anywhere routes the
  // keystroke into the composer. Skipped while another field is focused
  // (modals, refs popover, etc.) or when the textarea is covered by an
  // overlay — we use elementFromPoint instead of tracking modal state, so
  // it works for any current or future overlay without coupling. Stays
  // active during streaming so the user can compose / queue the next turn.
  useEffect(() => {
    const onDocKey = (e: globalThis.KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.isComposing) return;
      if (e.key.length !== 1) return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae) {
        const tag = ae.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (ae.isContentEditable) return;
      }
      const ta = textareaRef.current;
      if (!ta || ta.disabled) return;
      const rect = ta.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const top = document.elementFromPoint(
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
      );
      if (top !== ta && !(top && ta.contains(top))) return;
      ta.focus();
    };
    document.addEventListener('keydown', onDocKey);
    return () => document.removeEventListener('keydown', onDocKey);
  }, []);

  const submit = () => {
    const text = draft.trim();
    if (!text && attached.length === 0) return;
    // Image attachments go via the API's `attachments[]` so the server
    // can presign + emit `image_url` content blocks for the vision
    // model. Non-image refs (md/txt/pdf/etc.) stay as a one-line
    // `attached: …` prelude so the agent decides whether to call
    // `read_ref` based on the user's actual question.
    const isImage = (name: string) =>
      /\.(jpe?g|png|webp|gif)$/i.test(name);
    const imageRefs = attached.filter(isImage);
    const textRefs = attached.filter((n) => !isImage(n));
    const prelude =
      textRefs.length > 0 ? `attached: ${textRefs.join(', ')}\n\n` : '';
    // While busy, App.tsx parks this into queuedSendByThread and the
    // in-flight turn's finally block fires it. Either way we clear the
    // local draft so the user can keep composing the next thought.
    onSend(prelude + text, imageRefs.length > 0 ? { imageAttachments: imageRefs } : undefined);
    setDraft('');
    setAttached([]);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey &&
      !e.nativeEvent.isComposing
    ) {
      e.preventDefault();
      submit();
    }
  };

  // Counts dragenter/dragleave so leaving a child element doesn't flicker
  // off the visual highlight. dragover is fired continuously and sets the
  // dropEffect — without it some browsers default to "move" and refuse the
  // drop with no error.
  const onDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (!projectId) return;
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!projectId) return;
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const uploadFiles = async (files: File[]) => {
    if (!projectId || files.length === 0) return;
    setUploadError(null);
    setUploading((n) => n + files.length);
    // Upload sequentially: order matters for the chip row, and parallel
    // uploads don't really help the user since multipart posts are tiny.
    for (const f of files) {
      try {
        const entry = await api.uploadRef(projectId, f);
        setAttached((prev) => (prev.includes(entry.name) ? prev : [...prev, entry.name]));
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : String(err));
      } finally {
        setUploading((n) => Math.max(0, n - 1));
      }
    }
  };

  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    if (!projectId) return;
    await uploadFiles(Array.from(e.dataTransfer.files));
  };

  // Pasting from clipboard. Two cases worth caring about:
  //   (1) screenshot paste — Chromium synthesizes File("image.png"), so two
  //       pastes would collide on the server (`write_ref` is idempotent
  //       overwrite). Stamp every clipboard file with a timestamp before
  //       upload to keep them distinct.
  //   (2) text-only paste — fall through to the textarea's default handler
  //       so plain text still pastes normally.
  const onPaste = async (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!projectId) return;
    const cd = e.clipboardData;
    if (!cd) return;
    const collected: File[] = Array.from(cd.files ?? []);
    if (collected.length === 0 && cd.items) {
      for (const item of Array.from(cd.items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f) collected.push(f);
        }
      }
    }
    if (collected.length === 0) return;
    e.preventDefault();
    const stamp = Date.now().toString(36);
    const renamed = collected.map((f, i) => {
      const dot = f.name.lastIndexOf('.');
      const base = dot > 0 ? f.name.slice(0, dot) : f.name || 'pasted';
      const ext = dot > 0 ? f.name.slice(dot) : '';
      const suffix = collected.length > 1 ? `${stamp}-${i}` : stamp;
      return new File([f], `${base}-${suffix}${ext}`, { type: f.type });
    });
    await uploadFiles(renamed);
  };

  const removeAttached = (name: string) => {
    setAttached((prev) => prev.filter((n) => n !== name));
  };

  const hasContent = draft.trim().length > 0 || attached.length > 0;

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{ position: 'relative' }}
    >
      {(attached.length > 0 || uploading > 0 || uploadError || queuedSend) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            marginBottom: 6,
            alignItems: 'center',
          }}
        >
          {queuedSend && (
            <span
              className="mono"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                padding: '2px 4px 2px 8px',
                background: 'rgba(59,130,246,0.06)',
                border: '1px dashed var(--blue)',
                borderRadius: 12,
                color: 'var(--ink-1)',
                maxWidth: '100%',
              }}
              title={t('discussion.queuedChipTooltip')}
            >
              {t('discussion.queuedPrefix')} {truncate(
                queuedSend.text.replace(/^attached: [^\n]*\n\n/, '').trim()
                  || (queuedSend.imageRefs.length === 1
                    ? t('discussion.queuedImageOne', { count: queuedSend.imageRefs.length })
                    : t('discussion.queuedImageMany', { count: queuedSend.imageRefs.length })),
                60,
              )}
              <button
                onClick={() => onCancelQueuedSend?.()}
                style={{
                  fontSize: 11,
                  lineHeight: 1,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--ink-3)',
                  cursor: 'pointer',
                  padding: '0 4px',
                }}
                aria-label={t('discussion.queuedCancelAria')}
              >
                ×
              </button>
            </span>
          )}
          {attached.map((name) => (
            <span
              key={name}
              className="mono"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                padding: '2px 4px 2px 8px',
                background: 'var(--paper-1)',
                border: '1px solid var(--rule)',
                borderRadius: 12,
                color: 'var(--ink-1)',
              }}
              title={t('discussion.attachedChipTooltip')}
            >
              📎 {name}
              <button
                onClick={() => removeAttached(name)}
                style={{
                  fontSize: 11,
                  lineHeight: 1,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--ink-3)',
                  cursor: 'pointer',
                  padding: '0 4px',
                }}
                aria-label={t('discussion.attachedRemoveAria', { name })}
              >
                ×
              </button>
            </span>
          ))}
          {uploading > 0 && (
            <span
              className="mono"
              style={{ fontSize: 10.5, color: 'var(--ink-3)', padding: '0 4px' }}
            >
              {t('discussion.uploading', { count: uploading })}
            </span>
          )}
          {uploadError && (
            <span
              className="mono"
              style={{
                fontSize: 10.5,
                color: 'var(--red, #d23f3f)',
                padding: '0 4px',
                cursor: 'pointer',
              }}
              onClick={() => setUploadError(null)}
              title={t('discussion.clickToDismiss')}
            >
              {t('discussion.uploadFailed', { error: uploadError })}
            </span>
          )}
        </div>
      )}
      {/* Single-row inline composer: textarea + 📎 + send share one
          rounded frame. textarea auto-grows via CSS `field-sizing: content`
          (no JS) and is wrapped in a flex:1 div so it's NEVER directly a
          flex item — that's what avoided the previous scrollHeight blow-up.
          Buttons use `align-self: flex-end` so they pin to the bottom-right
          when textarea grows multi-line. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          border: `1px ${dragOver ? 'dashed' : 'solid'} ${
            dragOver ? 'var(--blue)' : focused ? 'var(--blue)' : 'var(--rule)'
          }`,
          borderRadius: 8,
          background: dragOver ? 'rgba(59,130,246,0.06)' : 'white',
          transition: 'border-color 80ms, background 80ms',
        }}
      >
        {/* textarea wrapper — flex:1 belongs HERE, not on the textarea
            itself. Keeps the textarea in normal block flow inside this
            div so `field-sizing: content` works naturally. */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
          <textarea
            ref={textareaRef}
            value={draft}
            rows={1}
            autoComplete="off"
            inputMode="text"
            enterKeyHint="send"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            placeholder={
              dragOver
                ? t('discussion.composerDropPlaceholder')
                : t('discussion.composerPlaceholder')
            }
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              fontSize: 13.5,
              lineHeight: 1.45,
              resize: 'none',
              overflowY: 'auto',
              border: 'none',
              outline: 'none',
              padding: '8px 12px',
              background: 'transparent',
              color: 'var(--ink-1)',
              // CSS auto-grow. rows=1 sets the floor (~22px content);
              // browser sizes up to maxHeight then scrolls inside. No JS.
              // `as` cast because @types/react doesn't list field-sizing
              // yet — it's a 2024 spec landed in all major browsers.
              ...(({ fieldSizing: 'content' } as unknown) as React.CSSProperties),
              maxHeight: 120,
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </div>
        {/* 📎 — anchored bottom-right via align-self when textarea grows. */}
        <div style={{ position: 'relative', flexShrink: 0, alignSelf: 'flex-end' }}>
          <button
            className="composer-action"
            onClick={() => {
              if (!projectId) return;
              setShowRefsPopover((v) => !v);
            }}
            disabled={!projectId}
            title={projectId ? t('discussion.attachRefTooltip') : t('discussion.openProjectFirst')}
            style={{
              fontSize: 14,
              padding: '6px 8px',
              margin: '0 2px 4px 0',
              borderRadius: 4,
              border: 'none',
              background: showRefsPopover ? 'var(--paper-1)' : 'transparent',
              color: 'var(--ink-1)',
              cursor: projectId ? 'pointer' : 'not-allowed',
              lineHeight: 1,
            }}
            onMouseEnter={(e) => {
              if (projectId && !showRefsPopover) {
                (e.currentTarget as HTMLElement).style.background = 'var(--paper-1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showRefsPopover) {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }
            }}
          >
            📎
          </button>
          {showRefsPopover && projectId && (
            <RefsPopover
              projectId={projectId}
              onAttach={(name) => {
                setAttached((prev) =>
                  prev.includes(name) ? prev : [...prev, name],
                );
              }}
              onClose={() => setShowRefsPopover(false)}
            />
          )}
        </div>
        {(() => {
          // Three button modes:
          //   1. busy + onStopTurn available → red "stop ■" interrupts
          //      the in-flight turn. Enabled regardless of textarea
          //      content; pressing Enter still queues a follow-up.
          //   2. busy + no onStopTurn (turn_id not yet captured, ~10ms
          //      window after startTurn) → fall through to the original
          //      "queue" button so the user always has an action.
          //   3. not busy → "send" submits.
          const stopMode = busy && !!onStopTurn;
          const onClick = stopMode ? () => onStopTurn?.() : submit;
          const disabled = stopMode ? false : !hasContent;
          const title = stopMode
            ? t('discussion.sendButtonStopTooltip')
            : busy
              ? t('discussion.sendButtonQueueTooltip')
              : t('discussion.sendButtonSendTooltip');
          const background = stopMode
            ? 'var(--red, #dc2626)'
            : !hasContent
              ? 'var(--ink-4)'
              : busy
                ? 'var(--blue, #3b82f6)'
                : 'var(--ink-0)';
          const cursor = disabled ? 'not-allowed' : 'pointer';
          return (
            <button
              className="composer-action"
              onClick={onClick}
              disabled={disabled}
              title={title}
              aria-label={stopMode ? t('discussion.sendButtonStopAria') : title}
              style={{
                flexShrink: 0,
                alignSelf: 'flex-end',
                fontSize: 12,
                padding: '6px 12px',
                margin: '0 4px 4px 0',
                borderRadius: 6,
                border: 'none',
                background,
                color: 'white',
                cursor,
                lineHeight: 1.2,
                fontWeight: 600,
                letterSpacing: '.04em',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <span>{stopMode ? t('discussion.sendButtonStop') : busy ? t('discussion.sendButtonQueue') : t('discussion.sendButtonSend')}</span>
              <span style={{ fontSize: 11, opacity: 0.85 }}>
                {stopMode ? '■' : '↑'}
              </span>
            </button>
          );
        })()}
      </div>
    </div>
  );
}

const fmt = (ts: number) => {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const truncate = (s: string, n: number) => (s.length > n ? s.slice(0, n) + '…' : s);

/// Decides whether the "✦ thinking" placeholder should render. Hides it
/// when the agent is already actively producing something the user can
/// see (a live thinking bubble or an assistant text bubble), since the
/// placeholder would just duplicate the signal. Shows it during the
/// pre-first-token gap (waiting for GLM to respond at all).
function shouldShowConnectingPlaceholder(messages: ChatMessage[]): boolean {
  if (messages.length === 0) return true;
  const last = messages[messages.length - 1];
  return last.kind !== 'assistant' && last.kind !== 'thinking';
}

/** Score an assistant message body for "structured listicle" features —
 *  numbered/bulleted item count, heading depth, tier markers, and length.
 *  Returns the score plus whether it qualifies for the inline "Convert
 *  to gaps" button.
 *
 *  Heuristics chosen to catch the common shapes ("4-tier priority audit",
 *  "X breakdown of N items", "list of gaps with descriptions") while
 *  ignoring short conversational replies even if they contain a stray
 *  bullet or two. The threshold (≥6 items + length ≥ 600 chars) was set
 *  by eyeballing real production transcripts; tune as we get more data. */
function scoreListicle(text: string): { qualifies: boolean; itemCount: number; score: number } {
  if (!text || text.length < 400) {
    return { qualifies: false, itemCount: 0, score: 0 };
  }

  // Per-line item markers. Numbered list (1. / 1、 / 1）), bullet (- / * ),
  // and Chinese ordinal (一、二、三、…) all count.
  const lines = text.split('\n');
  let itemCount = 0;
  for (const ln of lines) {
    const t = ln.trim();
    if (/^\d+[.、)）]\s/.test(t)) itemCount++;
    else if (/^[-*•]\s/.test(t)) itemCount++;
    else if (/^[一二三四五六七八九十][、.]\s/.test(t)) itemCount++;
  }

  const headingCount = (text.match(/^#{1,4}\s/gm) || []).length;
  // Tier markers: red/yellow/green/white circles, "第N层", "优先级", priority labels.
  const tierMarkers =
    (text.match(/[🔴🟡🟢⚪🟠🔵🟣]/gu) || []).length +
    (text.match(/第[一二三四五]层/g) || []).length +
    (text.match(/优先级/g) || []).length +
    (text.match(/\bP[0-3]\b/g) || []).length;

  const score = itemCount + headingCount * 1.5 + tierMarkers * 2;
  // Threshold tuned to fire on the user's actual gap-audit replies
  // (~22 items + tier markers) and stay quiet on short Q-and-A.
  const qualifies = itemCount >= 6 && score >= 8 && text.length >= 600;

  return { qualifies, itemCount, score };
}

/** Inline banner shown right after the user clicks Resolve on a pinned gap.
 *  The discussion just produced cards that closed the gap — that's a high-
 *  signal moment to ask "did anything new surface?" An audit run now will
 *  see fresh cards + the cleared gap context and surface anything the
 *  closure exposed (e.g. resolving "资源表" reveals "数值平衡" is now needed).
 *
 *  Auto-dismisses after 30s so it doesn't pile up on quiet projects.
 *  No close button on the right — Dismiss is a button so the user
 *  unambiguously expresses "no thanks" instead of guessing the X behavior.
 */
function PostResolveAuditBanner({
  gapId,
  onAudit,
  onDismiss,
}: {
  gapId: string;
  onAudit?: () => void;
  onDismiss?: () => void;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!onDismiss) return;
    const timer = setTimeout(onDismiss, 30000);
    return () => clearTimeout(timer);
  }, [gapId, onDismiss]);

  return (
    <div
      style={{
        padding: '8px 18px',
        background: 'rgba(99,102,241,.06)',
        borderBottom: '1px solid rgba(99,102,241,.18)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 12,
        color: 'var(--ink-1)',
      }}
    >
      <span style={{ color: '#4f46e5', fontSize: 13 }}>↻</span>
      <span>
        {t('discussion.postResolvePrefix')}{' '}
        <code style={{ color: 'var(--ink-3)' }}>{gapId}</code>
        {t('discussion.postResolveSuffix')}
      </span>
      <span style={{ flex: 1 }} />
      <button
        onClick={onAudit}
        className="mono"
        style={{
          fontSize: 10.5,
          padding: '3px 10px',
          border: '1px solid #4f46e5',
          borderRadius: 3,
          background: '#4f46e5',
          color: 'white',
          cursor: 'pointer',
          letterSpacing: '.06em',
          fontWeight: 600,
        }}
      >
        {t('discussion.runAudit')}
      </button>
      <button
        onClick={onDismiss}
        className="mono"
        style={{
          fontSize: 10.5,
          padding: '3px 8px',
          border: '1px solid var(--rule)',
          borderRadius: 3,
          background: 'white',
          color: 'var(--ink-2)',
          cursor: 'pointer',
          letterSpacing: '.06em',
        }}
      >
        {t('discussion.postResolveDismiss')}
      </button>
    </div>
  );
}
